import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { type InputNumberHookOptions, type InputNumberProps, useInputNumber } from '@pkg/primitives';
import { clamp } from '@pkg/utils';

import { Action, ActionText, PrimitiveInputNumber, StyledFieldNumber } from './FieldNumber.style';
import { tokens } from './FieldNumber.tokens';

// TODO: `size` prop should be restricted to certain values.
export interface FieldNumberProps extends InputNumberHookOptions {
    id?: InputNumberProps['id'];
    name?: InputNumberProps['name'];
    placeholder?: InputNumberProps['placeholder'];
    size?: number;
    growToLength?: number;
    autoFocus?: InputNumberProps['autoFocus'];
    error?: boolean;
    stretch?: boolean;
    onPending?: (pending: boolean) => void;
}

export function FieldNumber({
    id,
    name,
    placeholder,
    growToLength,
    size = tokens.size.preference,
    autoFocus = false,
    error = false,
    stretch = false,
    onPending,
    ...hookProps
}: FieldNumberProps) {
    const {
        displayValue,
        safeMax,
        safeStep,
        disabledDecrement,
        disabledIncrement,
        pending,
        handleFocus,
        handleBlur,
        handleChange,
        decrementing,
        handleDecrementOnce,
        handleDecrementRepeat,
        handleDecrementRepeatReset,
        incrementing,
        handleIncrementOnce,
        handleIncrementRepeat,
        handleIncrementRepeatReset,
    } = useInputNumber({ ...hookProps });

    const hasMounted = useRef(false);
    const [width, setWidth] = useState<number>();

    const valueLength = displayValue?.toString().length || 1;
    const minLength = Math.max(growToLength ?? 0, 1);
    const allowedLength = Math.min(valueLength, minLength);

    useLayoutEffect(() => {
        // `width` will only ever be either `undefined`,
        // or a value greater than `0` (no negative values).
        if (growToLength && growToLength > 0) {
            setWidth(clamp(tokens.charWidth.min, allowedLength + 1, tokens.charWidth.max));
        }

        return () => setWidth(undefined);
    }, [growToLength, allowedLength]);

    useEffect(() => {
        if (hasMounted.current) onPending?.(pending);
        hasMounted.current = true;
    }, [pending, onPending]);

    return (
        <StyledFieldNumber
            $charWidth={width}
            $size={clamp(tokens.size.min, size, tokens.size.max)}
            $error={error}
            $stretch={stretch}
        >
            <Action
                disabled={disabledDecrement}
                onAction={handleDecrementOnce}
                onPointerDown={handleDecrementRepeat}
                onPointerUp={handleDecrementRepeatReset}
                onPointerLeave={decrementing ? handleDecrementRepeatReset : undefined}
            >
                <ActionText>&ndash;</ActionText>
            </Action>

            <PrimitiveInputNumber
                value={displayValue}
                id={id}
                name={name}
                placeholder={placeholder}
                min={hookProps.min}
                max={safeMax}
                step={safeStep}
                autoFocus={autoFocus}
                disabled={hookProps.disabled}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onChange={handleChange}
            />

            <Action
                disabled={disabledIncrement}
                onAction={handleIncrementOnce}
                onPointerDown={handleIncrementRepeat}
                onPointerUp={handleIncrementRepeatReset}
                onPointerLeave={incrementing ? handleIncrementRepeatReset : undefined}
            >
                <ActionText>+</ActionText>
            </Action>
        </StyledFieldNumber>
    );
}
